import React, { useState, useEffect, useRef } from "react";
import { FaBell, FaCheckDouble, FaCircle } from "react-icons/fa";
import API_BASE_URL from "../../config/api";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";

function NotificationPanel() {
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [isOpen, setIsOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const panelRef = useRef(null);
    const { user, isAuthenticated } = useAuth();

    useEffect(() => {
        if (isAuthenticated) {
            fetchNotifications();

            const intervalId = setInterval(() => {
                fetchNotifications();
            }, 60000);

            const handleClickOutside = (event) => {
                if (
                    panelRef.current &&
                    !panelRef.current.contains(event.target)
                ) {
                    setIsOpen(false);
                }
            };

            document.addEventListener("mousedown", handleClickOutside);

            return () => {
                clearInterval(intervalId);
                document.removeEventListener("mousedown", handleClickOutside);
            };
        }
    }, [isAuthenticated, user]);

    const fetchNotifications = async () => {
        try {
            setLoading(true);

            if (!user || (!user.userId && !user._id)) {
                setNotifications([]);
                setUnreadCount(0);
                setLoading(false);
                return;
            }

            const userId = user.userId || user._id;

            let endpoint = `${API_BASE_URL}/api/projects/announcements/general`;
            if (user.role === "student" || user.role === "sa") {
                endpoint = `${API_BASE_URL}/api/projects/user/${userId}/announcements`;
            }

            const response = await axios.get(endpoint);
            const data = response.data;

            const storageKey = `readNotifications_${userId}`;
            const readNotificationsStr = sessionStorage.getItem(storageKey);
            const readNotifications = readNotificationsStr
                ? JSON.parse(readNotificationsStr)
                : [];

            const processedNotifications = data.map((notification) => ({
                ...notification,
                isRead: readNotifications.includes(notification._id),
            }));

            setNotifications(processedNotifications);
            setUnreadCount(
                processedNotifications.filter((n) => !n.isRead).length
            );
        } catch (error) {
            console.error("Error fetching notifications:", error);
            setNotifications([]);
            setUnreadCount(0);
        } finally {
            setLoading(false);
        }
    };

    const togglePanel = () => {
        setIsOpen(!isOpen);
    };

    const markAsRead = (id) => {
        if (!user || (!user.userId && !user._id)) return;

        const userId = user.userId || user._id;

        setNotifications((prevNotifications) =>
            prevNotifications.map((notification) =>
                notification._id === id
                    ? { ...notification, isRead: true }
                    : notification
            )
        );

        setUnreadCount((prev) => Math.max(0, prev - 1));

        const storageKey = `readNotifications_${userId}`;
        const readNotificationsStr = sessionStorage.getItem(storageKey);
        const readNotifications = readNotificationsStr
            ? JSON.parse(readNotificationsStr)
            : [];

        if (!readNotifications.includes(id)) {
            readNotifications.push(id);
            sessionStorage.setItem(
                storageKey,
                JSON.stringify(readNotifications)
            );
        }
    };

    const markAllAsRead = () => {
        if (!user || (!user.userId && !user._id)) return;

        const userId = user.userId || user._id;

        setNotifications((prevNotifications) =>
            prevNotifications.map((notification) => ({
                ...notification,
                isRead: true,
            }))
        );

        setUnreadCount(0);

        const storageKey = `readNotifications_${userId}`;
        const notificationIds = notifications.map((n) => n._id);
        sessionStorage.setItem(storageKey, JSON.stringify(notificationIds));
    };

    if (!isAuthenticated) {
        return null;
    }

    return (
        <div className="relative inline-block z-50" ref={panelRef}>
            <button
                className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-all focus:outline-none"
                onClick={togglePanel}
                type="button"
                aria-label="Notifications"
            >
                <FaBell className="text-xl" />
                {unreadCount > 0 && (
                    <span className="absolute top-0 right-0 transform translate-x-1/4 -translate-y-1/4 flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 bg-[#B7202E] text-white text-[10px] font-bold rounded-full border-2 border-white shadow-sm">
                        {unreadCount}
                    </span>
                )}
            </button>

            {isOpen && (
                <div className="absolute top-12 right-0 w-80 md:w-96 max-h-[30rem] bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden flex flex-col animate-fade-in origin-top-right">
                    <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50 backdrop-blur-sm sticky top-0 z-10">
                        <h3 className="font-bold text-gray-800 flex items-center gap-2">
                            Start Notifications
                        </h3>
                        {unreadCount > 0 && (
                            <button
                                className="text-xs text-[#161dc7] font-medium hover:underline flex items-center gap-1 bg-blue-50 px-2 py-1 rounded transition-colors hover:bg-blue-100"
                                onClick={markAllAsRead}
                            >
                                <FaCheckDouble /> Mark all read
                            </button>
                        )}
                    </div>

                    <div className="overflow-y-auto flex-1 custom-scrollbar">
                        {loading ? (
                            <div className="p-8 text-center text-gray-500 text-sm">
                                <div className="w-6 h-6 border-2 border-gray-200 border-t-[#B7202E] rounded-full animate-spin mx-auto mb-2"></div>
                                Loading...
                            </div>
                        ) : notifications.length > 0 ? (
                            <div className="divide-y divide-gray-50">
                                {notifications.map((notification, index) => (
                                    <div
                                        key={notification._id || notification.id || index}
                                        className={`p-4 cursor-pointer transition-all hover:bg-gray-50 group relative ${!notification.isRead ? "bg-blue-50/30" : ""
                                            }`}
                                        onClick={() => markAsRead(notification._id || notification.id)}
                                    >
                                        <div className="flex gap-3 items-start">
                                            <div className="mt-1">
                                                {!notification.isRead ? (
                                                    <FaCircle className="text-[10px] text-[#161dc7]" />
                                                ) : (
                                                    <div className="w-2.5 h-2.5 rounded-full bg-gray-200"></div>
                                                )}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex justify-between items-start mb-1">
                                                    <h4 className={`text-sm font-semibold truncate pr-2 ${!notification.isRead ? 'text-gray-900' : 'text-gray-600'}`}>
                                                        {notification.title || notification.type || "Notification"}
                                                    </h4>
                                                    <span className="text-[10px] text-gray-400 whitespace-nowrap bg-gray-100 px-1.5 py-0.5 rounded">
                                                        {new Date(notification.date).toLocaleDateString("en-IN", {
                                                            day: "numeric",
                                                            month: "short",
                                                            year: "numeric",
                                                        })}
                                                    </span>
                                                </div>
                                                <p className={`text-sm leading-relaxed ${!notification.isRead ? 'text-gray-700' : 'text-gray-500'}`}>
                                                    {notification.message || "No details available"}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="p-12 text-center text-gray-400">
                                <FaBell className="text-4xl mx-auto mb-3 text-gray-200" />
                                <p className="text-sm">No notifications yet</p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default NotificationPanel;
